'use client';

// Client login form island (T0801, split from the former page.tsx). All auth
// logic lives here; it posts credentials to the same-origin BFF route handler
// /api/auth/login (proxies Fastify, sets the httpOnly cookies). No token in
// client JS. Generic error on 401 (anti-enumeration), distinct on 423/502. The
// submit button + focus ring use the per-commune CSS vars (bg-primary /
// ring-primary) injected by the root layout.
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface LoginResponse {
  user?: { role: string };
  error?: string;
  retryAfter?: string | number;
}

function roleToPath(role: string): string {
  // V1: every role lands on the same placeholder. Per-role routes
  // (/admin, /mes-demandes, ...) get wired here once they exist.
  switch (role) {
    default:
      return '/tableau-de-bord';
  }
}

const GENERIC_ERROR = 'Identifiants invalides.';

export function LoginForm() {
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (isSubmitting) return;
    setError(null);
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email, motDePasse: password }),
      });

      let data: LoginResponse = {};
      try {
        data = (await response.json()) as LoginResponse;
      } catch {
        // Tolerate an empty/non-JSON body.
      }

      if (response.ok && data.user) {
        router.push(roleToPath(data.user.role));
        router.refresh();
        return;
      }

      if (response.status === 423) {
        setError(
          'Compte temporairement bloqué après plusieurs tentatives. Réessayez plus tard.',
        );
      } else if (response.status === 502) {
        setError('Service momentanément indisponible. Réessayez dans un instant.');
      } else {
        // 401 / 400 / anything else -> generic message (anti-enumeration).
        setError(GENERIC_ERROR);
      }
    } catch {
      setError('Service momentanément indisponible. Réessayez dans un instant.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="mt-8 space-y-5">
      <div className="space-y-1.5">
        <Label htmlFor="email">Adresse e-mail</Label>
        <Input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isSubmitting}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="password">Mot de passe</Label>
        <Input
          id="password"
          name="password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={isSubmitting}
        />
        <p className="text-right text-sm">
          <Link href="/reset" className="text-primary font-medium hover:underline">
            Mot de passe oublié ?
          </Link>
        </p>
      </div>

      {error !== null && (
        <p role="alert" className="text-destructive text-sm">
          {error}
        </p>
      )}

      <Button type="submit" size="lg" disabled={isSubmitting} className="w-full">
        {isSubmitting ? 'Connexion…' : 'Se connecter'}
      </Button>
    </form>
  );
}
